import Dexie, { type Table } from "dexie";
import type { KeyVault } from "@roqeet/core";

/**
 * 키 볼트 구현 — 결정 #15(다층 방어).
 *
 * 정직한 전제(결정 #15): 순수 클라이언트 SPA에서 같은 출처의 코드로부터 키를
 * 완벽히 숨길 수는 없다. 목표는 위험을 "낮추는 것"이다. 적용한 계층:
 *  - 평상시: Web Crypto 비추출(non-extractable) AES-GCM 마스터 키로 암호화.
 *    이 키는 IndexedDB에 CryptoKey 객체로 보관되며 raw 바이트를 꺼낼 수 없어,
 *    디스크/IndexedDB 덤프만으로는 평문이 나오지 않는다.
 *  - 옵션: 패스프레이즈(PBKDF2 → AES-GCM). 켜면 마스터 키 대신 패스프레이즈
 *    파생 키로 재암호화하고, 이때만 동기화 옵트인이 가능(결정 #15/#20).
 *
 * 메인 앱 DB(roqeet-rp)와 분리된 별도 DB(roqeet-vault)에 저장한다. 키는
 * 기본적으로 기기 로컬이며 드라이브 동기화에서 제외된다(결정 #8/#15/#20).
 */

interface VaultMeta {
  id: "config";
  passphraseEnabled: boolean;
  /** PBKDF2 salt (패스프레이즈 사용 시). */
  salt?: Uint8Array;
  /** 패스프레이즈 검증용: 알려진 평문을 파생 키로 암호화한 값. */
  verifier?: { iv: Uint8Array; ct: Uint8Array };
}

interface VaultSecret {
  keyRef: string;
  iv: Uint8Array;
  ct: Uint8Array;
}

interface VaultMasterKey {
  id: "master";
  /** 비추출 AES-GCM CryptoKey. 패스프레이즈 미사용 시에만 존재. */
  key: CryptoKey;
}

class VaultDB extends Dexie {
  meta!: Table<VaultMeta, string>;
  secrets!: Table<VaultSecret, string>;
  masterKey!: Table<VaultMasterKey, string>;

  constructor() {
    super("roqeet-vault");
    this.version(1).stores({
      meta: "id",
      secrets: "keyRef",
      masterKey: "id",
    });
  }
}

const VERIFIER_PLAINTEXT = "roqeet-vault-ok";

function getSubtle(): SubtleCrypto {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) throw new Error("Web Crypto subtle 사용 불가 환경입니다.");
  return c.subtle;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function fromBase64(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/**
 * TS 5.7+ typed-array 제네릭 대응. IndexedDB에서 꺼낸 Uint8Array 및
 * TextEncoder.encode() 결과는 `Uint8Array<ArrayBufferLike>`라 SubtleCrypto가
 * 요구하는 `BufferSource`(ArrayBuffer 백킹)와 타입이 어긋난다. 새 ArrayBuffer로
 * 복사해 안전화한다. (core/util/hash.ts의 hashBytes와 동일한 회피책.)
 */
function ab(view: Uint8Array): ArrayBuffer {
  const out = new Uint8Array(view.byteLength);
  out.set(view);
  return out.buffer;
}

/** 문자열을 ArrayBuffer(UTF-8)로 인코딩(subtle crypto 입력용). */
function encBytes(s: string): ArrayBuffer {
  return ab(enc.encode(s));
}

export class WebCryptoKeyVault implements KeyVault {
  private readonly db = new VaultDB();
  /** 현재 활성 암호화 키(메모리 전용). 잠금 시 null. */
  private activeKey: CryptoKey | null = null;

  isUnlocked(): boolean {
    return this.activeKey !== null;
  }

  async hasPassphrase(): Promise<boolean> {
    const meta = await this.db.meta.get("config");
    return !!meta?.passphraseEnabled;
  }

  /** 패스프레이즈가 없으면 마스터 키로 자동 언락. 있으면 패스프레이즈 검증. */
  async unlock(passphrase?: string): Promise<boolean> {
    const meta = await this.db.meta.get("config");
    if (!meta?.passphraseEnabled) {
      this.activeKey = await this.ensureMasterKey();
      return true;
    }
    if (!passphrase) return false;
    const derived = await this.deriveKey(passphrase, meta.salt!);
    // 검증자 복호화로 패스프레이즈 정확성 확인.
    if (meta.verifier) {
      try {
        const pt = await getSubtle().decrypt(
          { name: "AES-GCM", iv: ab(meta.verifier.iv) },
          derived,
          ab(meta.verifier.ct),
        );
        if (dec.decode(pt) !== VERIFIER_PLAINTEXT) return false;
      } catch {
        return false;
      }
    }
    this.activeKey = derived;
    return true;
  }

  lock(): void {
    this.activeKey = null;
  }

  async setPassphrase(oldPassphrase: string | undefined, newPassphrase: string): Promise<void> {
    // 기존 시크릿을 모두 평문으로 복호화하기 위해 현재 키로 언락.
    const plaintexts = await this.decryptAll(oldPassphrase);

    const salt = crypto.getRandomValues(new Uint8Array(16));
    const derived = await this.deriveKey(newPassphrase, salt);

    // 검증자 생성.
    const vIv = crypto.getRandomValues(new Uint8Array(12));
    const vCt = new Uint8Array(
      await getSubtle().encrypt({ name: "AES-GCM", iv: vIv }, derived, encBytes(VERIFIER_PLAINTEXT)),
    );

    // 새 키로 전부 재암호화.
    await this.reencryptAll(plaintexts, derived);

    await this.db.meta.put({
      id: "config",
      passphraseEnabled: true,
      salt,
      verifier: { iv: vIv, ct: vCt },
    });
    // 패스프레이즈 모드에서는 마스터 키 제거(파생 키만 사용).
    await this.db.masterKey.delete("master");
    this.activeKey = derived;
  }

  async removePassphrase(passphrase: string): Promise<void> {
    const plaintexts = await this.decryptAll(passphrase);
    const master = await this.ensureMasterKey(true);
    await this.reencryptAll(plaintexts, master);
    await this.db.meta.put({ id: "config", passphraseEnabled: false });
    this.activeKey = master;
  }

  async store(secret: string): Promise<string> {
    await this.ensureUnlocked();
    const keyRef = crypto.randomUUID();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(
      await getSubtle().encrypt({ name: "AES-GCM", iv }, this.activeKey!, encBytes(secret)),
    );
    await this.db.secrets.put({ keyRef, iv, ct });
    return keyRef;
  }

  async resolve(keyRef: string): Promise<string | undefined> {
    if (!this.activeKey) {
      // 패스프레이즈 미사용이면 자동 언락 시도.
      const ok = await this.unlock();
      if (!ok || !this.activeKey) return undefined;
    }
    const row = await this.db.secrets.get(keyRef);
    if (!row) return undefined;
    try {
      const pt = await getSubtle().decrypt(
        { name: "AES-GCM", iv: ab(row.iv) },
        this.activeKey,
        ab(row.ct),
      );
      return dec.decode(pt);
    } catch {
      return undefined;
    }
  }

  async remove(keyRef: string): Promise<void> {
    await this.db.secrets.delete(keyRef);
  }

  /** 패스프레이즈가 설정된 경우에만 암호문 묶음을 내보낸다(결정 #15). */
  async exportWrapped(): Promise<Record<string, string> | undefined> {
    const meta = await this.db.meta.get("config");
    if (!meta?.passphraseEnabled) return undefined;
    const rows = await this.db.secrets.toArray();
    const out: Record<string, string> = {};
    for (const r of rows) {
      out[r.keyRef] = JSON.stringify({ iv: toBase64(r.iv), ct: toBase64(r.ct) });
    }
    return out;
  }

  async importWrapped(data: Record<string, string>): Promise<void> {
    const rows: VaultSecret[] = [];
    for (const [keyRef, blob] of Object.entries(data)) {
      const parsed = JSON.parse(blob) as { iv: string; ct: string };
      rows.push({ keyRef, iv: fromBase64(parsed.iv), ct: fromBase64(parsed.ct) });
    }
    await this.db.secrets.bulkPut(rows);
  }

  // ---- 내부 헬퍼 ----

  private async ensureUnlocked(): Promise<void> {
    if (this.activeKey) return;
    const ok = await this.unlock();
    if (!ok) throw new Error("키 볼트가 잠겨 있습니다. 패스프레이즈로 잠금 해제하세요.");
  }

  /** 비추출 마스터 키를 가져오거나 생성한다. */
  private async ensureMasterKey(forceNew = false): Promise<CryptoKey> {
    if (!forceNew) {
      const existing = await this.db.masterKey.get("master");
      if (existing) return existing.key;
    }
    const key = await getSubtle().generateKey({ name: "AES-GCM", length: 256 }, false, [
      "encrypt",
      "decrypt",
    ]);
    await this.db.masterKey.put({ id: "master", key });
    return key;
  }

  /** PBKDF2로 패스프레이즈 → AES-GCM 키 파생. */
  private async deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
    const baseKey = await getSubtle().importKey(
      "raw",
      encBytes(passphrase),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    return getSubtle().deriveKey(
      { name: "PBKDF2", salt: ab(salt), iterations: 150_000, hash: "SHA-256" },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  }

  /** 모든 시크릿을 현재(또는 주어진 패스프레이즈) 키로 복호화해 평문 맵 반환. */
  private async decryptAll(passphrase?: string): Promise<Map<string, string>> {
    const ok = await this.unlock(passphrase);
    if (!ok || !this.activeKey) {
      throw new Error("기존 키로 잠금 해제할 수 없습니다. 패스프레이즈를 확인하세요.");
    }
    const rows = await this.db.secrets.toArray();
    const out = new Map<string, string>();
    for (const r of rows) {
      const pt = await getSubtle().decrypt(
        { name: "AES-GCM", iv: ab(r.iv) },
        this.activeKey,
        ab(r.ct),
      );
      out.set(r.keyRef, dec.decode(pt));
    }
    return out;
  }

  /** 평문 맵을 새 키로 재암호화해 저장한다. */
  private async reencryptAll(plaintexts: Map<string, string>, key: CryptoKey): Promise<void> {
    const rows: VaultSecret[] = [];
    for (const [keyRef, secret] of plaintexts) {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ct = new Uint8Array(
        await getSubtle().encrypt({ name: "AES-GCM", iv }, key, encBytes(secret)),
      );
      rows.push({ keyRef, iv, ct });
    }
    await this.db.secrets.bulkPut(rows);
  }
}
